import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { KoobButton, KoobInputLabel } from '@koob/ui';
import { useTranslation } from 'react-i18next';

/**
 * Shared shell for the hotels/experiences/library search bars: primary
 * criteria live in the bar, secondary refinements behind a single
 * FiltersPopover (Airbnb-style hierarchy).
 */
export const FiltersBar = ({ children }) => (
  <div className="rounded-sm border-0 border-t-4 border-solid border-brand bg-white px-5 py-2.5 shadow-sm [&_input::placeholder]:text-foreground">
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

// KoobInputSelect/KoobInputDate portal their menus to <body>; interacting
// with them must not dismiss the filters popover, so ignore outside events
// originating from any Radix popper portal.
const isInRadixPortal = event =>
  event.target instanceof Element &&
  Boolean(event.target.closest('[data-radix-popper-content-wrapper]'));

export const FiltersPopover = ({ count = 0, onClear, children }) => {
  const { t } = useTranslation('misc');
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <KoobButton
          variant="secondary"
          style={{ height: 42, borderRadius: 9999 }}
        >
          <i className="fa-light fa-sliders" style={{ fontSize: 16 }} />
          <span>{t('filters.title')}</span>
          {count > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1.5 text-xs font-semibold text-white">
              {count}
            </span>
          )}
        </KoobButton>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          collisionPadding={16}
          className="z-popover w-[640px] max-w-[calc(100vw-2rem)] rounded-xl border border-solid border-gray-200 bg-white p-5 shadow-xl"
          onPointerDownOutside={event => {
            if (isInRadixPortal(event)) {
              event.preventDefault();
            }
          }}
          onFocusOutside={event => {
            if (isInRadixPortal(event)) {
              event.preventDefault();
            }
          }}
        >
          <div className="flex max-h-[60vh] flex-col gap-5 overflow-y-auto pr-1">
            {children}
          </div>
          <div className="mt-5 flex items-center justify-between border-0 border-t border-solid border-gray-100 pt-4">
            <KoobButton variant="ghost" onClick={onClear}>
              {t('filters.clearAll')}
            </KoobButton>
            <KoobButton variant="primary" onClick={() => setOpen(false)}>
              {t('filters.showResults')}
            </KoobButton>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

FiltersPopover.Section = function FiltersPopoverSection({ label, children }) {
  return <KoobInputLabel label={label}>{children}</KoobInputLabel>;
};

// Two fields on one line (stacks on very narrow popovers).
FiltersPopover.Row = function FiltersPopoverRow({ children }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
  );
};
